import {
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import WhiteCard from "../WhiteCard";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { OpenWith } from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { PatientProfile } from "src/api/types";
import { isNonProviderView } from "src/utils/isNonProviderView";
import PatientInvoices from "../PatientInvoices";
import PatientDisputes from "../PatientDisputes";
import AddNewPayment from "../AddNewPayment";
import { useManageModal } from "src/hooks/useManageModal";
import StripeContextProvider from "src/context/StripeContext";
import ChargePatientModal from "../Modals/ChargePatientModal";

interface Props {
  patient: PatientProfile;
}

const tabs = ["INVOICES", "DISPUTES"];

const PatientPayments = ({ patient }: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [value, setValue] = useState<number>(0);
  const [open, { handleClose, handleOpen }] = useManageModal();
  const [
    openCharge,
    { handleClose: handleCloseCharge, handleOpen: handleOpenCharge },
  ] = useManageModal();

  const stripeUrl = useMemo(
    () =>
      import.meta.env.PROD
        ? `https://dashboard.stripe.com/customers/${patient?.stripe_customer_id}`
        : `https://dashboard.stripe.com/test/customers/${patient?.stripe_customer_id}`,
    [patient],
  );

  const isNPV = useMemo(() => {
    return isNonProviderView(pathname);
  }, [pathname]);

  const handleExpand = useCallback(() => {
    return navigate(value === 0 ? "invoices" : "disputes");
  }, [navigate, value]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
    },
    [],
  );

  return (
    <>
      <WhiteCard
        width="320px"
        maxHeight="400px"
        gap={1}
        sx={{ overflowY: "auto", overflowX: "auto" }}
      >
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} gap={2} alignItems="center">
            <Typography variant="subtitle2">PAYMENTS</Typography>
            <Link to={stripeUrl} target="_blank">
              <Button variant="secondary">
                <Typography fontWeight={700} variant="body1">
                  {"Billing History (Stripe)"}
                </Typography>
              </Button>
            </Link>
          </Stack>
          {isNPV ? (
            <Stack direction={"row"} alignItems="center">
              <Button
                variant="text"
                color="secondary"
                onClick={handleOpenCharge}
              >
                Charge Patient
              </Button>
              <Button variant="text" onClick={handleOpen}>
                + Add Payment
              </Button>
              <Tooltip title="Expand" sx={{ marginLeft: "auto" }}>
                <IconButton onClick={handleExpand}>
                  <OpenWith />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Stack>
        <Tabs value={value} onChange={handleChange}>
          {tabs.map((t) => (
            <Tab label={t} key={t} />
          ))}
        </Tabs>
        {value === 0 ? (
          <PatientInvoices patient={patient} />
        ) : (
          <PatientDisputes patient={patient} />
        )}
      </WhiteCard>
      <StripeContextProvider>
        <AddNewPayment open={open} onClose={handleClose} patient={patient} />
      </StripeContextProvider>
      <ChargePatientModal
        open={openCharge}
        onClose={handleCloseCharge}
        patient={patient}
      />
    </>
  );
};

export default PatientPayments;
